import React, { useState } from "react";

export default function MatchCard({
	match,
	mode,
	redThreshold = 0.7,
	yellowThreshold = 0.3,
}) {
	const existing = match.existing || {};
	const newLead = match.new || {};

	// Pull the matched domain from the backend; include a few safe fallbacks.
	const matchedDomainRaw =
		(match && (match.matched_domain || match.domain)) ||
		existing.matched_domain ||
		newLead.matched_domain ||
		"";
	const matchedDomain = String(matchedDomainRaw || "").trim();
	const domainFound = Boolean(matchedDomain);

	const [openExisting, setOpenExisting] = useState(false);
	const [openNew, setOpenNew] = useState(false);
	const [selectedLabel, setSelectedLabel] = useState(null);

	const allFields = [
		["First Name", "first_name"],
		["Last Name", "last_name"],
		["Company Name", "company_name"],
		["Email", "email"], // special handling: add badge + domain row below
		["Address", "address"],
		["City", "city"],
		["Normalized Phone", "normalized_phone"],
	];

	const alwaysShowKeys = [
		"company_name",
		"email",
		"address",
		"city",
		"normalized_phone",
	];

	const EmailDomainBadge = ({ found, domain }) =>
		found ? (
			<span
				style={{
					marginLeft: 8,
					fontSize: 11,
					color: "#155724",
					background: "#d4edda",
					border: "1px solid #c3e6cb",
					borderRadius: 999,
					padding: "1px 8px",
					whiteSpace: "nowrap",
				}}
				title={`Matched domain: ${domain}`}>
				{domain}
			</span>
		) : null;

	const DomainMatchedRow = ({ found, domain }) => (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				padding: "6px 0",
				borderBottom: "1px solid #eee",
				marginTop: -1, // visually ties it to the Email row
			}}>
			<div
				style={{
					fontWeight: 600,
					color: "#555",
					flexBasis: "45%",
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}>
				Domain (matched)
			</div>
			<div
				style={{
					flexBasis: "55%",
					overflow: "hidden",
					textOverflow: "ellipsis",
					color: found ? "#222" : "#888",
					fontWeight: 400,
					whiteSpace: "nowrap",
				}}
				title={found ? domain : "—"}>
				{found ? domain : "—"}
			</div>
		</div>
	);

	const renderDropdown = (lead, title, bg, isOpen, toggleOpen) => (
		<div
			style={{
				flex: 1,
				background: bg,
				borderRadius: 6,
				boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
				padding: "10px 14px",
				boxSizing: "border-box",
				fontSize: 13,
				display: "flex",
				flexDirection: "column",
				minWidth: 240,
			}}>
			<button
				onClick={toggleOpen}
				aria-expanded={isOpen}
				style={{
					background: "none",
					border: "none",
					padding: 0,
					marginBottom: 10,
					textAlign: "left",
					fontWeight: 700,
					fontSize: 16,
					cursor: "pointer",
					color: "#222",
					display: "flex",
					alignItems: "center",
					gap: 6,
					userSelect: "none",
				}}>
				<span>{title}</span>
				<span
					style={{
						display: "inline-block",
						transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
						transition: "transform 0.25s ease",
						fontSize: 18,
						lineHeight: 1,
					}}>
					▶
				</span>
			</button>

			{/* Lead ID */}
			{lead.lead_id && (
				<div
					style={{
						marginBottom: 4,
						padding: "2px 0",
						fontWeight: 600,
						color: "#333",
						borderBottom: "1px solid #eee",
					}}>
					🆔 Lead ID: {lead.lead_id}
				</div>
			)}

			{/* Association */}
			{lead.association && (
				<div
					style={{
						marginBottom: 8,
						padding: "2px 0",
						fontWeight: 600,
						color: "#333",
						borderBottom: "1px solid #eee",
					}}>
					🏷️ Association: {String(lead.association).trim()}
				</div>
			)}

			{allFields.map(([label, key]) => {
				const alwaysShow = alwaysShowKeys.includes(key);
				if (!alwaysShow && !isOpen) return null;

				const value = lead[key] || "-";
				const isEmailRow = key === "email";

				return (
					<React.Fragment key={label}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								padding: "6px 0",
								borderBottom: "1px solid #eee",
								alignItems: "flex-start",
								gap: 10,
							}}>
							<div
								style={{
									fontWeight: 600,
									color: "#555",
									flexBasis: "45%",
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
									paddingTop: 2,
								}}>
								{label}
							</div>
							<div
								style={{
									flexBasis: "55%",
									overflow: "hidden",
									textOverflow: "ellipsis",
									color: "#222",
									fontWeight: 400,
									whiteSpace: "nowrap",
								}}
								title={String(value)}>
								<span
									style={{
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										verticalAlign: "middle",
									}}>
									{value}
								</span>
								{/* small badge next to email, if we have a matched domain */}
								{isEmailRow && (
									<EmailDomainBadge
										found={domainFound}
										domain={matchedDomain}
									/>
								)}
							</div>
						</div>

						{/* Insert a dedicated "Domain (matched)" row immediately after Email */}
						{isEmailRow && (
							<DomainMatchedRow found={domainFound} domain={matchedDomain} />
						)}
					</React.Fragment>
				);
			})}
		</div>
	);

	const score = match.match_score;
	let scoreColor = "#c8f7c5";
	let scoreLabel = "New Lead";

	if (score >= redThreshold) {
		scoreColor = "#ffcaca";
		scoreLabel = "Duplicate";
	} else if (score >= yellowThreshold) {
		scoreColor = "#fff5b1";
		scoreLabel = "Needs Review";
	}

	const handleLabel = (label) => {
		setSelectedLabel(label);
		console.log("Labeled as:", label, match);
	};

	return (
		<div
			style={{
				display: "flex",
				alignItems: "flex-start",
				borderBottom: "1px solid #ddd",
				padding: 12,
				gap: 20,
				fontFamily:
					"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
			}}>
			{/* ⬅️ New Lead on the LEFT now */}
			{renderDropdown(newLead, "New Lead", "#f0f8ff", openNew, () =>
				setOpenNew(!openNew)
			)}

			{/* ➡️ Existing on the RIGHT (only if present) */}
			{existing && Object.keys(existing).length > 0
				? renderDropdown(
						existing,
						"Existing Lead",
						"#f9f9f9",
						openExisting,
						() => setOpenExisting(!openExisting)
				  )
				: null}

			<div
				style={{
					minWidth: 140,
					display: "flex",
					flexDirection: "column",
					gap: 10,
				}}>
				{/* Score Box */}
				<div
					style={{
						padding: "8px 12px",
						background: scoreColor,
						borderRadius: 6,
						boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						fontWeight: 700,
						color: "#333",
						userSelect: "none",
						whiteSpace: "nowrap",
					}}>
					<div style={{ fontSize: 16 }}>Score: {score.toFixed(4)}</div>
					<div style={{ fontSize: 12, marginTop: 4 }}>{scoreLabel}</div>
				</div>

				{/* Label Buttons (Training mode only) */}
				{mode === "train" && (
					<div
						className="fade-in"
						style={{
							display: "flex",
							gap: 8,
							justifyContent: "center",
							animation: "fadeIn 0.4s ease-in-out",
						}}>
						{[
							{ label: "Y", desc: "Yes - Confirm this is a duplicate" },
							{ label: "N", desc: "No - Confirm this is NOT a duplicate" },
							{
								label: "U",
								desc: "Unsure - Unable to determine duplicate status",
							},
						].map(({ label, desc }) => {
							const isSelected = selectedLabel === label;
							const color =
								label === "Y"
									? "#4caf50"
									: label === "N"
									? "#f44336"
									: "#ffeb3b";
							const textColor = label === "U" ? "#000" : "#fff";

							return (
								<button
									key={label}
									onClick={() => handleLabel(label)}
									title={desc}
									style={{
										background: color,
										color: textColor,
										border: "none",
										borderRadius: 4,
										padding: isSelected ? "6px 12px" : "4px 10px",
										fontWeight: isSelected ? "bold" : "normal",
										cursor: "pointer",
										transition: "all 0.2s ease",
										boxShadow: isSelected ? "0 0 4px rgba(0,0,0,0.3)" : "none",
										transform: isSelected ? "scale(1.05)" : "scale(1)",
									}}>
									{label}
								</button>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
